import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from '@acitra/i18n';
import { useAuthStore } from '../stores/auth-store';
import { AUTH_CONSTRAINTS } from '@acitra/shared';
import { IntuitEmailNotVerifiedModal } from '../components/IntuitEmailNotVerifiedModal';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const newPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(
        AUTH_CONSTRAINTS.MIN_PASSWORD_LENGTH,
        `Password must be at least ${AUTH_CONSTRAINTS.MIN_PASSWORD_LENGTH} characters`
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type LoginFormData = z.infer<typeof loginSchema>;
type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showIntuitEmailNotVerified, setShowIntuitEmailNotVerified] = useState(false);

  const {
    isAuthenticated,
    isLoading,
    error,
    needsNewPassword,
    tenants,
    loginWithEmail,
    loginWithIntuit,
    completeNewPassword,
    clearError,
  } = useAuthStore();

  // Check for auth error from callback redirect (e.g., Intuit email not verified)
  useEffect(() => {
    const authErrorType = sessionStorage.getItem('auth_error_type');
    if (authErrorType) {
      sessionStorage.removeItem('auth_error_type');
      if (authErrorType === 'intuit_email_not_verified') {
        setShowIntuitEmailNotVerified(true);
      }
    }
  }, []);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const newPasswordForm = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  // Redirect after successful login
  useEffect(() => {
    if (isAuthenticated) {
      // Check if there's a pending invitation to return to
      const invitationToken = sessionStorage.getItem('invitation_return_token');
      if (invitationToken) {
        sessionStorage.removeItem('invitation_return_token');
        navigate(`/invite/${invitationToken}`, { replace: true });
        return;
      }

      // Get connected tenants (not disconnected)
      const connectedTenants = tenants.filter((t) => !t.quickbooks?.disconnectedAt);

      // If no connected tenants, go to company selection to connect or reconnect
      if (connectedTenants.length === 0) {
        navigate('/companies', { replace: true });
        return;
      }

      // Find MRU (most recently used) tenant among connected tenants
      const sortedByLastUsed = [...connectedTenants].sort((a, b) => {
        const aLastUsed = (a as { lastUsedAt?: string }).lastUsedAt;
        const bLastUsed = (b as { lastUsedAt?: string }).lastUsedAt;
        if (!aLastUsed && !bLastUsed) return 0;
        if (!aLastUsed) return 1;
        if (!bLastUsed) return -1;
        return new Date(bLastUsed).getTime() - new Date(aLastUsed).getTime();
      });

      const mruTenant = sortedByLastUsed[0];
      const hasMru = !!(mruTenant as { lastUsedAt?: string }).lastUsedAt;

      // If there's a MRU tenant, or there's only one connected tenant, go to loading
      if (hasMru || connectedTenants.length === 1) {
        navigate(`/tenant/${mruTenant.id}/loading`, { replace: true });
      } else {
        // Multiple connected tenants but no MRU - let user choose
        navigate('/companies', { replace: true });
      }
    }
  }, [isAuthenticated, tenants, navigate]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleLogin = async (data: LoginFormData) => {
    await loginWithEmail(data.email, data.password);
  };

  const handleNewPassword = async (data: NewPasswordFormData) => {
    await completeNewPassword(data.newPassword);
  };

  const handleIntuitLogin = () => {
    loginWithIntuit();
  };

  // Show new password form if required
  if (needsNewPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set New Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please set a new password to continue
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <form
            className="mt-8 space-y-6"
            onSubmit={newPasswordForm.handleSubmit(handleNewPassword)}
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  {...newPasswordForm.register('newPassword')}
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {newPasswordForm.formState.errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {newPasswordForm.formState.errors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <input
                  {...newPasswordForm.register('confirmPassword')}
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {newPasswordForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {newPasswordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting password...' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main login form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('common.appName')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">Sign in to manage your invoices</p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Intuit Sign In Button */}
        <div>
          <button
            type="button"
            onClick={handleIntuitLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center py-3 px-4 border rounded-md shadow-sm bg-white text-sm font-medium hover:bg-[#0077C5]/10 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderColor: '#0077C5', color: '#0077C5' }}
          >
            Sign in with Intuit
          </button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gray-50 text-gray-500">Or continue with email</span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form className="mt-8 space-y-6" onSubmit={loginForm.handleSubmit(handleLogin)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                {...loginForm.register('email')}
                type="email"
                autoComplete="email"
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="you@company.com"
              />
              {loginForm.formState.errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {loginForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                {...loginForm.register('password')}
                type="password"
                autoComplete="current-password"
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              {loginForm.formState.errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {loginForm.formState.errors.password.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end">
            <a href="/forgot-password" className="text-sm text-primary-600 hover:text-primary-500">
              Forgot your password?
            </a>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>

      {/* Auth error modals */}
      <IntuitEmailNotVerifiedModal
        isOpen={showIntuitEmailNotVerified}
        onClose={() => setShowIntuitEmailNotVerified(false)}
      />
    </div>
  );
}
